import { Link } from "@inertiajs/react"
import GoogleMapReact from "google-map-react"
import { Building2, ChevronRight, MapPin, X } from "lucide-react"
import { useCallback, useRef, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { brokerPropertyPath } from "@/routes"
import type { Property } from "@/types"

// Prefer a project-specific key via VITE_GOOGLE_MAPS_API_KEY; falls back to the
// shared Brem Google Maps key so the map works out of the box in development.
const GOOGLE_MAPS_API_KEY =
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? "AIzaSyDq1DViCbZdI8WDWaMShJwd9vfGddpGSM4"

// Center of the continental US, used before markers are fit into view.
const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 }
const DEFAULT_ZOOM = 4

interface MarkerProps {
  lat: number
  lng: number
  isSelected: boolean
  onSelect: () => void
}

function Marker({ isSelected, onSelect }: MarkerProps) {
  return (
    <button
      type="button"
      onClick={(e) => {
        // Prevent the map's onClick (which clears selection) from firing.
        e.stopPropagation()
        onSelect()
      }}
      aria-label="Property location"
      className={`absolute -translate-x-1/2 -translate-y-full cursor-pointer transition-transform duration-150 hover:scale-110 ${
        isSelected ? "z-20 scale-110" : "z-10"
      }`}
    >
      <MapPin
        className={`h-8 w-8 drop-shadow-md ${
          isSelected ? "fill-primary text-primary-foreground" : "fill-primary/90 text-primary-foreground"
        }`}
        strokeWidth={1.5}
      />
    </button>
  )
}

interface PropertyMapProps {
  properties: Property[]
}

export default function PropertyMap({ properties }: PropertyMapProps) {
  const [selected, setSelected] = useState<Property | null>(null)
  const mapsApi = useRef<{ map: unknown; maps: unknown } | null>(null)

  const located = properties.filter(
    (p) => p.latitude != null && p.longitude != null
  )

  const fitToMarkers = useCallback(
    ({ map, maps }: { map: any; maps: any }) => {
      mapsApi.current = { map, maps }
      if (located.length === 0) return

      const bounds = new maps.LatLngBounds()
      located.forEach((p) => bounds.extend({ lat: p.latitude!, lng: p.longitude! }))
      map.fitBounds(bounds)

      // Don't zoom in too far when there's only a single property.
      maps.event.addListenerOnce(map, "idle", () => {
        if (map.getZoom() > 15) map.setZoom(15)
      })
    },
    [located]
  )

  if (located.length === 0) {
    return (
      <div className="bg-muted/40 flex h-[600px] items-center justify-center rounded-xl border">
        <div className="text-center">
          <MapPin className="text-muted-foreground mx-auto mb-3 h-10 w-10" />
          <p className="text-sm font-medium">No mapped properties yet</p>
          <p className="text-muted-foreground text-sm">
            Properties appear here once their address has been geocoded.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative h-[600px] w-full overflow-hidden rounded-xl border">
      <GoogleMapReact
        bootstrapURLKeys={{ key: GOOGLE_MAPS_API_KEY }}
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={DEFAULT_ZOOM}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
          clickableIcons: false,
          styles: [
            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
          ],
        }}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={fitToMarkers}
        onClick={() => setSelected(null)}
      >
        {located.map((property) => (
          <Marker
            key={property.id}
            lat={property.latitude!}
            lng={property.longitude!}
            isSelected={selected?.id === property.id}
            onSelect={() => setSelected(property)}
          />
        ))}
      </GoogleMapReact>

      {selected && (
        <div className="absolute top-4 left-4 z-30 w-80 max-w-[calc(100%-2rem)]">
          <Card className="shadow-xl">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="leading-tight font-semibold">{selected.name}</h3>
                  <p className="text-muted-foreground text-sm">
                    {selected.city}, {selected.state}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setSelected(null)}
                  aria-label="Close"
                >
                  <X />
                </Button>
              </div>

              <p className="text-muted-foreground mt-1 text-xs">{selected.address}</p>

              <div className="mt-3 flex items-center justify-between">
                {(selected.available_count ?? 0) > 0 ? (
                  <Badge>{selected.available_count} available</Badge>
                ) : (
                  <Badge variant="secondary">No availability</Badge>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  render={<Link href={brokerPropertyPath(selected.id)} />}
                >
                  <Building2 />
                  View suites
                  <ChevronRight />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
